/**
 * Asset Data Quality tab content.
 *
 * Pulls a pre-bucketed source × month report from the backend
 * (`/reports/by-source-period?assetId=…`) so the frontend never paginates
 * through individual activities. Each row is one (scope × activityType ×
 * subType) combination with `totals[]` (CO₂e per bucket) and `counts[]`
 * (activities per bucket).
 *
 * Detection rules from `dataQualityDetection.ts` colour the per-cell dots.
 * The thresholds live in a modal opened via the grid header's Settings
 * button so the table itself stays uncluttered.
 */

import { useMemo, useState } from 'react';
import { Loader2, Settings2 } from 'lucide-react';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Progress } from '@g17eco/ui-modern/components/ui/progress';
import { TabNav, type TabItem } from '@g17eco/ui-modern/components/ui/tab-nav';
import {
  FUEL_TYPE_LABELS,
  GAS_TYPE_LABELS,
  PROCESS_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
} from '@g17eco/emissions-config';
import { useCreateActivityMutation, useGetEmissionsBySourcePeriodQuery } from '../../../api/emissions';
import { expandMonthToPeriodRange } from '../../../utils/activityUtils';
import {
  ACTIVITY_TYPE_BRANDING,
  ACTIVITY_TYPE_LABELS,
} from '../../../config/activityTypeBranding';
import { SCOPE_TAILWIND } from '../../../config/scopeBranding';
import {
  type ActivityUnit,
  type CreateActivityRequest,
  type EmissionAsset,
  EmissionScope,
  type FuelType,
  type FugitiveGasType,
  type ProcessType,
  type RefrigerantGasType,
  type SourcePeriodRow,
  type VehicleType,
} from '../../../types/emissions';
import type { AssetActivityEntry } from '../../form/types';
import { GapFillActivityModal, type GapFillContext } from './GapFillActivityModal';
import {
  DEFAULT_THRESHOLDS,
  type CellIssue,
  type DetectionThresholds,
  detectRowIssues,
  peakSeverity,
} from './dataQualityDetection';
import { EditActivityById } from './EditActivityById';
import { ActivityPickerModal } from './ActivityPickerModal';
import { DataQualityControlsModal } from './DataQualityControlsModal';
import { DataQualityGrid, type DataQualityRowDef } from './DataQualityGrid';

interface AssetDataQualityTabProps {
  initiativeId: string;
  asset: EmissionAsset;
  periodStart: string;
  periodEnd: string;
  /** All-time bounds derived from `availableYears`. When provided, enables the "All data" range toggle. */
  allTimePeriodStart?: string;
  allTimePeriodEnd?: string;
}

type RangeMode = 'fy' | 'all';

const SCOPE_LABEL: Record<EmissionScope, string> = {
  [EmissionScope.Scope1]: 'Scope 1',
  [EmissionScope.Scope2]: 'Scope 2',
  [EmissionScope.Scope3]: 'Scope 3',
};

/**
 * Resolve the user-facing subtype label for a row (e.g. `natural_gas` →
 * "Natural Gas"). Returns null when the row has no subtype.
 */
const resolveSubTypeLabel = (
  subTypeField: SourcePeriodRow['subTypeField'],
  subTypeValue: string | null,
): string | null => {
  if (!subTypeValue || !subTypeField) return null;
  // Why: each *_TYPE_LABELS record is keyed by its specific enum, but the API
  // surfaces `subTypeValue` as a plain `string`. The server only emits values
  // from the corresponding enum, so a single typed cast at the lookup is safe.
  switch (subTypeField) {
    case 'fuelType':
      return FUEL_TYPE_LABELS[subTypeValue as FuelType] ?? subTypeValue;
    case 'vehicleType':
      return VEHICLE_TYPE_LABELS[subTypeValue as VehicleType] ?? subTypeValue;
    case 'refrigerantType':
      return GAS_TYPE_LABELS[subTypeValue as RefrigerantGasType | FugitiveGasType] ?? subTypeValue;
    case 'processType':
      return PROCESS_TYPE_LABELS[subTypeValue as ProcessType] ?? subTypeValue;
    default:
      return subTypeValue;
  }
};

/**
 * Quick-and-readable error message extractor for RTK Query failures. The
 * backend returns `{ success: false, message: '…' }` on validation errors;
 * we surface the message verbatim, falling back to the HTTP status when
 * the body is empty.
 */
const extractErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Unknown error';
  const e = error as { status?: number | string; data?: unknown; error?: string };
  if (e.data && typeof e.data === 'object' && 'message' in e.data && typeof e.data.message === 'string') {
    return e.data.message;
  }
  if (typeof e.error === 'string') return e.error;
  if (e.status !== undefined) return `Request failed (${e.status})`;
  return 'Unknown error';
};

export const AssetDataQualityTab = ({
  initiativeId,
  asset,
  periodStart,
  periodEnd,
  allTimePeriodStart,
  allTimePeriodEnd,
}: AssetDataQualityTabProps) => {
  const [thresholds, setThresholds] = useState<DetectionThresholds>(DEFAULT_THRESHOLDS);
  const [gapDetectionEnabled, setGapDetectionEnabled] = useState(true);
  const [isControlsOpen, setIsControlsOpen] = useState(false);
  const [rangeMode, setRangeMode] = useState<RangeMode>('fy');
  const [gapFillContext, setGapFillContext] = useState<GapFillContext | null>(null);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
  const [pickerCtx, setPickerCtx] = useState<{ ids: string[]; title: string } | null>(null);
  const [createActivity] = useCreateActivityMutation();

  // Use the asset's own initiativeId — assets in a child reporting level
  // won't return data when queried against the URL's initiativeId.
  const assetInitiativeId = asset.initiativeId || initiativeId;

  // Resolve the requested range. "All data" is only enabled when the parent
  // has resolved the all-time bounds (otherwise we'd silently fall back to FY).
  const hasAllTime = Boolean(allTimePeriodStart && allTimePeriodEnd);
  const effectiveStart = rangeMode === 'all' && allTimePeriodStart ? allTimePeriodStart : periodStart;
  const effectiveEnd = rangeMode === 'all' && allTimePeriodEnd ? allTimePeriodEnd : periodEnd;

  // Server-side bucketing: one round-trip, no client-side pagination loop,
  // no proration logic to maintain. Always monthly — the user wants a bar
  // per calendar month even across multi-year all-time views (the chart
  // columns can shrink, but the resolution must not).
  const { data, isLoading, error } = useGetEmissionsBySourcePeriodQuery(
    {
      initiativeId: assetInitiativeId,
      periodStart: effectiveStart,
      periodEnd: effectiveEnd,
      granularity: 'monthly',
      assetId: asset._id,
    },
    { skip: !assetInitiativeId || !asset._id },
  );

  // Trim trailing buckets the user shouldn't see as "missing data":
  //   1. Anything whose bucket ends after now() (future months) — there's no
  //      data to be missing yet.
  //   2. Any all-rows-empty buckets that sit past the asset's last activity —
  //      the grid right-edge should align with the latest real entry, not
  //      the requested period end.
  // We walk from the right and stop at the first bucket where any row has
  // data; everything past that index is dropped.
  const visibleBucketCount = useMemo(() => {
    const buckets = data?.buckets ?? [];
    const sourceRows = data?.rows ?? [];
    if (buckets.length === 0) return 0;
    const nowMs = Date.now();
    let lastVisible = -1;
    for (let i = buckets.length - 1; i >= 0; i--) {
      const bucketEndMs = new Date(buckets[i].end).getTime();
      const isFuture = !Number.isNaN(bucketEndMs) && bucketEndMs > nowMs;
      const hasData = sourceRows.some((row) => (row.counts?.[i] ?? 0) > 0);
      if (!isFuture && hasData) {
        lastVisible = i;
        break;
      }
    }
    // No data at all — fall back to showing the full requested window so the
    // empty-state copy + "no activities" message render predictably.
    if (lastVisible < 0) return buckets.length;
    return lastVisible + 1;
  }, [data?.buckets, data?.rows]);

  const visibleBuckets = useMemo(
    () => (data?.buckets ?? []).slice(0, visibleBucketCount),
    [data?.buckets, visibleBucketCount],
  );
  const monthLabels = useMemo(() => visibleBuckets.map((b) => b.label), [visibleBuckets]);

  // Row key → source-row index, so the gap-click handler can look up scope /
  // activityType / subtype from the original API response without re-deriving
  // them.
  const sourceRowByKey = useMemo(() => {
    const map = new Map<string, SourcePeriodRow>();
    for (const row of data?.rows ?? []) {
      map.set(`${row.scope}__${row.activityType}__${row.subTypeValue ?? 'none'}`, row);
    }
    return map;
  }, [data?.rows]);

  const rows = useMemo<DataQualityRowDef[]>(() => {
    const sourceRows = data?.rows ?? [];
    return sourceRows
      .map((row) => {
        // Trim each row's per-bucket arrays to the visible window so the
        // detection logic and the grid never see future / post-last-activity
        // buckets at all.
        const trimmedTotals = (row.totals ?? []).slice(0, visibleBucketCount);
        const trimmedCounts = (row.counts ?? []).slice(0, visibleBucketCount);
        const trimmedActivityIds = (row.activityIds ?? []).slice(0, visibleBucketCount);

        // The backend leaves `null` in the totals/counts arrays for buckets the
        // row didn't touch (zero CO₂e + zero count). For detection we treat any
        // bucket with count = 0 as a "gap" (no activity) so the variance/PoP
        // rules don't fire on legitimate empty months.
        const values: Array<number | null> = trimmedTotals.map((v, i) => {
          const count = trimmedCounts[i] ?? 0;
          return count > 0 ? v : null;
        });
        const issues: CellIssue[][] = detectRowIssues(
          values.map((v) => ({ value: v })),
          thresholds,
        );

        const brand = ACTIVITY_TYPE_BRANDING[row.activityType];
        const scopeBrand = SCOPE_TAILWIND[row.scope];
        const subtypeLabel = resolveSubTypeLabel(row.subTypeField, row.subTypeValue);
        const activityLabel = ACTIVITY_TYPE_LABELS[row.activityType] ?? row.activityType.replace(/_/g, ' ');

        // Title prefers the specific subtype (Natural Gas, Diesel, R-410A); when
        // there's no subtype we fall back to the activity type itself. Subtitle
        // shows the activity type only when subtype is present, so the row never
        // repeats itself.
        const title = subtypeLabel ?? activityLabel;
        const subtitle = subtypeLabel ? activityLabel : undefined;

        return {
          key: `${row.scope}__${row.activityType}__${row.subTypeValue ?? 'none'}`,
          title,
          subtitle,
          Icon: brand?.Icon ?? ACTIVITY_TYPE_BRANDING.stationary_combustion.Icon,
          iconColor: brand?.iconColor ?? 'tw:text-muted-foreground',
          scopeLabel: SCOPE_LABEL[row.scope] ?? row.scope,
          scopeBadgeColor: scopeBrand?.icon ?? '',
          values,
          counts: trimmedCounts,
          activityIdsByBucket: trimmedActivityIds,
          issues,
        };
      });
  }, [data?.rows, thresholds, visibleBucketCount]);

  const totals = useMemo(() => {
    let critical = 0;
    let warning = 0;
    let clean = 0;
    let gaps = 0;
    let totalCells = 0;
    for (const row of rows) {
      for (let i = 0; i < row.values.length; i++) {
        totalCells++;
        if (row.values[i] === null) {
          gaps++;
          continue;
        }
        const peak = peakSeverity(row.issues[i] ?? []);
        if (peak === 'critical') critical++;
        else if (peak === 'warning') warning++;
        else clean++;
      }
    }
    const recordedCells = totalCells - gaps;
    const coveragePct = totalCells > 0 ? (recordedCells / totalCells) * 100 : 0;
    return { critical, warning, clean, gaps, totalCells, recordedCells, coveragePct };
  }, [rows]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <div className='tw:text-center tw:py-12 tw:text-muted-foreground'>
            <Loader2 className='tw:size-6 tw:animate-spin tw:mx-auto tw:mb-3' />
            Analysing activity data…
          </div>
        </CardContent>
      </Card>
    );
  }

  // Surface request failures (e.g. 400 from validation, 500 from server) so we
  // don't silently render an empty grid the way the old code did.
  if (error) {
    return (
      <Card>
        <CardContent>
          <div className='tw:text-center tw:py-10 tw:space-y-1'>
            <p className='tw:text-sm tw:font-medium tw:text-red-600 tw:mb-0'>Failed to load data quality report</p>
            <p className='tw:text-xs tw:text-muted-foreground tw:mb-0'>{extractErrorMessage(error)}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent>
          <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-8 tw:mb-0'>
            No activities recorded for this asset in the selected period — nothing to assess yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  const headerActions = (
    <div className='tw:flex tw:items-center tw:gap-2'>
      {hasAllTime && (
        <RangeToggle value={rangeMode} onChange={setRangeMode} />
      )}
      <Button variant='outline' size='sm' onClick={() => setIsControlsOpen(true)}>
        <Settings2 className='tw:size-3.5 tw:mr-1' />
        Detection settings
      </Button>
    </div>
  );

  const handleGapClick = (rowKey: string, monthIndex: number) => {
    const sourceRow = sourceRowByKey.get(rowKey);
    const bucket = visibleBuckets[monthIndex];
    if (!sourceRow || !bucket) return;

    // Resolve a friendly title from the row, mirroring the grid's title logic.
    const subtypeLabel = (() => {
      if (!sourceRow.subTypeValue) return null;
      switch (sourceRow.subTypeField) {
        case 'fuelType':
          return FUEL_TYPE_LABELS[sourceRow.subTypeValue as FuelType] ?? sourceRow.subTypeValue;
        case 'vehicleType':
          return VEHICLE_TYPE_LABELS[sourceRow.subTypeValue as VehicleType] ?? sourceRow.subTypeValue;
        case 'refrigerantType':
          return GAS_TYPE_LABELS[sourceRow.subTypeValue as RefrigerantGasType | FugitiveGasType] ?? sourceRow.subTypeValue;
        case 'processType':
          return PROCESS_TYPE_LABELS[sourceRow.subTypeValue as ProcessType] ?? sourceRow.subTypeValue;
        default:
          return sourceRow.subTypeValue;
      }
    })();
    const activityLabel = ACTIVITY_TYPE_LABELS[sourceRow.activityType] ?? sourceRow.activityType.replace(/_/g, ' ');
    const title = subtypeLabel ? `${subtypeLabel} — ${activityLabel}` : activityLabel;

    // The bucket's start is an ISO datetime; the wizard's `<input type="month">`
    // expects YYYY-MM. Slicing to the first 7 chars works for both
    // `2025-01-01T00:00:00.000Z` (string) and `2025-01-01` (Date.toJSON-ish).
    const periodStart = new Date(bucket.start).toISOString().slice(0, 7);
    const periodEnd = new Date(new Date(bucket.end).getTime() - 1).toISOString().slice(0, 7);

    setGapFillContext({
      scope: sourceRow.scope,
      activityType: sourceRow.activityType,
      subTypeField: sourceRow.subTypeField,
      subTypeValue: sourceRow.subTypeValue,
      title,
      periodStart,
      periodEnd,
    });
  };

  /**
   * Click on a cell with data → open the activity drawer (single contributor)
   * or the picker modal (multiple contributors). The grid only fires this
   * handler when `activityIds[bucketIndex].length > 0`, so the empty-list
   * branch below is just defensive.
   */
  const handleCellClick = (rowKey: string, monthIndex: number) => {
    const sourceRow = sourceRowByKey.get(rowKey);
    const bucket = visibleBuckets[monthIndex];
    if (!sourceRow || !bucket) return;
    const ids = sourceRow.activityIds?.[monthIndex] ?? [];
    if (ids.length === 0) return;
    if (ids.length === 1) {
      setSelectedActivityId(ids[0]);
      return;
    }
    // Multi-activity bucket — small picker so the user can choose. Title
    // mirrors the cell tooltip ("Subtype — Mar '25") so users know where the
    // picker came from.
    const subtypeLabel = (() => {
      if (!sourceRow.subTypeValue) return null;
      switch (sourceRow.subTypeField) {
        case 'fuelType':
          return FUEL_TYPE_LABELS[sourceRow.subTypeValue as FuelType] ?? sourceRow.subTypeValue;
        case 'vehicleType':
          return VEHICLE_TYPE_LABELS[sourceRow.subTypeValue as VehicleType] ?? sourceRow.subTypeValue;
        case 'refrigerantType':
          return GAS_TYPE_LABELS[sourceRow.subTypeValue as RefrigerantGasType | FugitiveGasType] ?? sourceRow.subTypeValue;
        case 'processType':
          return PROCESS_TYPE_LABELS[sourceRow.subTypeValue as ProcessType] ?? sourceRow.subTypeValue;
        default:
          return sourceRow.subTypeValue;
      }
    })();
    const activityLabel = ACTIVITY_TYPE_LABELS[sourceRow.activityType] ?? sourceRow.activityType.replace(/_/g, ' ');
    const title = `${subtypeLabel ?? activityLabel} · ${bucket.label}`;
    setPickerCtx({ ids, title });
  };

  const handlePersistGapActivity = async (entry: AssetActivityEntry): Promise<{ activityId: string }> => {
    const { periodStart: ps, periodEnd: pe } = expandMonthToPeriodRange(entry.periodStart, entry.periodEnd);
    const data: CreateActivityRequest = {
      scope: entry.scope,
      activityType: entry.activityType,
      periodStart: ps,
      periodEnd: pe,
      quantity: Number(entry.quantity),
      unit: entry.unit as ActivityUnit,
      region: entry.region,
      regionFallback: entry.regionFallback,
      yearFallback: entry.yearFallback,
      assetId: asset._id,
      ...(entry.fuelType ? { fuelType: entry.fuelType as FuelType } : {}),
      ...(entry.vehicleType ? { vehicleType: entry.vehicleType as VehicleType } : {}),
      ...(entry.refrigerantType ? { refrigerantType: entry.refrigerantType as RefrigerantGasType } : {}),
      ...(entry.processType ? { processType: entry.processType as ProcessType } : {}),
      ...(entry.name ? { name: entry.name.trim() } : {}),
      ...(entry.dataSource ? { dataSource: entry.dataSource.trim() } : {}),
      ...(entry.notes ? { notes: entry.notes.trim() } : {}),
    };
    const result = await createActivity({ initiativeId: assetInitiativeId, data }).unwrap();
    return { activityId: result.data._id };
  };

  return (
    <div className='tw:space-y-4'>
      {/* Summary chip strip + coverage bar */}
      <Card>
        <CardContent className='tw:py-4 tw:space-y-3'>
          <div className='tw:flex tw:items-center tw:flex-wrap tw:gap-x-6 tw:gap-y-2'>
            <SummaryChip count={totals.critical} label='critical' dotClass='tw:bg-red-500' textClass='tw:text-red-600' />
            <SummaryChip
              count={totals.warning}
              label={totals.warning === 1 ? 'warning' : 'warnings'}
              dotClass='tw:bg-amber-500'
              textClass='tw:text-amber-600'
            />
            <SummaryChip count={totals.clean} label='clean' dotClass='tw:bg-emerald-500' textClass='tw:text-emerald-600' />
            {gapDetectionEnabled && (
              <SummaryChip
                count={totals.gaps}
                label={totals.gaps === 1 ? 'gap' : 'gaps'}
                dotClass='tw:bg-red-300'
                textClass='tw:text-red-500'
              />
            )}
          </div>
          <div className='tw:flex tw:items-center tw:gap-3'>
            <div className='tw:min-w-[140px]'>
              <div className='tw:text-xs tw:text-muted-foreground'>Overall coverage</div>
              <div className='tw:text-lg tw:font-bold'>
                {totals.coveragePct.toFixed(0)}%
                <span className='tw:text-xs tw:font-normal tw:text-muted-foreground tw:ml-2'>
                  {totals.recordedCells} of {totals.totalCells} expected entries
                </span>
              </div>
            </div>
            <Progress value={totals.coveragePct} className='tw:flex-1 tw:h-2' progressColor='#10b981' />
          </div>
        </CardContent>
      </Card>

      <DataQualityGrid
        rows={rows}
        monthLabels={monthLabels}
        gapDetectionEnabled={gapDetectionEnabled}
        headerActions={headerActions}
        onGapClick={gapDetectionEnabled ? handleGapClick : undefined}
        onCellClick={handleCellClick}
      />

      <DataQualityControlsModal
        isOpen={isControlsOpen}
        onClose={() => setIsControlsOpen(false)}
        thresholds={thresholds}
        onChange={setThresholds}
        gapDetectionEnabled={gapDetectionEnabled}
        onGapDetectionChange={setGapDetectionEnabled}
      />

      <GapFillActivityModal
        isOpen={gapFillContext !== null}
        onClose={() => setGapFillContext(null)}
        asset={asset}
        initiativeId={assetInitiativeId}
        context={gapFillContext}
        onPersistActivity={handlePersistGapActivity}
      />

      <ActivityPickerModal
        isOpen={pickerCtx !== null}
        onClose={() => setPickerCtx(null)}
        activityIds={pickerCtx?.ids ?? []}
        initiativeId={assetInitiativeId}
        title={pickerCtx?.title ?? ''}
        onSelect={(id) => {
          setPickerCtx(null);
          setSelectedActivityId(id);
        }}
      />

      <EditActivityById
        activityId={selectedActivityId}
        onClose={() => setSelectedActivityId(null)}
        initiativeId={assetInitiativeId}
      />
    </div>
  );
};

interface SummaryChipProps {
  count: number;
  label: string;
  dotClass: string;
  textClass: string;
}

const SummaryChip = ({ count, label, dotClass, textClass }: SummaryChipProps) => (
  <div className='tw:flex tw:items-center tw:gap-2'>
    <span className={`tw:size-2 tw:rounded-full ${dotClass}`} />
    <span className={`tw:text-sm tw:font-semibold ${textClass}`}>{count}</span>
    <span className='tw:text-sm tw:text-muted-foreground'>{label}</span>
  </div>
);

interface RangeToggleProps {
  value: RangeMode;
  onChange: (next: RangeMode) => void;
}

const RANGE_TABS: TabItem<RangeMode>[] = [
  { key: 'fy', label: 'Current FY' },
  { key: 'all', label: 'All data' },
];

/**
 * Two-segment switch: "Current FY" widens to "All data" when the user wants
 * a long-horizon view. Granularity auto-adapts (see `pickGranularity`) so
 * the All-data view doesn't fan out into 60+ unreadable monthly columns.
 */
const RangeToggle = ({ value, onChange }: RangeToggleProps) => (
  <TabNav variant='toggle' size='xs' tabs={RANGE_TABS} activeTab={value} onChange={onChange} />
);
